/**
 * Project Questions List Page
 * 
 * ΔD5.1: Questions list with review actions
 * Shows only active questions ordered by orderIndex/orderSubIndex
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getFirestore } from 'firebase/firestore';
import { 
  getProject,
  listActiveQuestions,
  type RbProject,
  type RbQuestion
} from '@threadlock/data';
import { ROUTES } from '@threadlock/routing';

export default function ProjectQuestionsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [project, setProject] = useState<RbProject | null>(null);
  const [questions, setQuestions] = useState<RbQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) return;

    const db = getFirestore();

    Promise.all([
      getProject(db, projectId),
      listActiveQuestions(db, projectId),
    ])
      .then(([proj, qs]) => {
        if (!proj) {
          setError('Project not found');
          setLoading(false);
          return;
        }

        setProject(proj);
        setQuestions(qs);
        setLoading(false);
      })
      .catch((err) => {
        console.error('[ProjectQuestionsPage] Error loading data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load questions');
        setLoading(false);
      });
  }, [projectId]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="animate-pulse">
          <div className="h-8 bg-neutral-200 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-neutral-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-800">{error || 'Project not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-neutral-900">Questions</h1>
            <p className="mt-2 text-sm text-neutral-600">
              <Link
                to={ROUTES.PROJECT_DETAIL.replace(':projectId', project.id)}
                className="text-blue-600 hover:text-blue-700"
              >
                {project.title}
              </Link>
            </p>
          </div>
          <Link
            to={ROUTES.PROJECT_DETAIL.replace(':projectId', project.id)}
            className="inline-flex items-center text-sm text-neutral-600 hover:text-neutral-900"
          >
            ← Back to Project
          </Link>
        </div>

        {questions.length === 0 ? (
          <div className="bg-white border border-neutral-200 rounded-lg p-8 text-center">
            <p className="text-neutral-600">No questions extracted yet.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {questions.map((question, index) => (
              <div
                key={question.id}
                className="bg-white border border-neutral-200 rounded-lg p-6 hover:border-blue-300 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-neutral-500">
                      #{index + 1}
                    </span>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        question.state === 'active'
                          ? 'bg-green-100 text-green-800'
                          : question.state === 'ignored'
                          ? 'bg-neutral-100 text-neutral-600'
                          : 'bg-neutral-50 text-neutral-500'
                      }`}
                    >
                      {question.state}
                    </span>
                    {question.operation !== 'extract' && (
                      <span className="text-xs text-neutral-500">
                        ({question.operation})
                      </span>
                    )}
                  </div>
                </div>

                <div className="mb-4">
                  <p className="text-sm text-neutral-900 whitespace-pre-wrap">
                    {question.questionText}
                  </p>
                  {question.extractedText !== question.questionText && (
                    <details className="mt-2">
                      <summary className="text-xs text-neutral-500 cursor-pointer">
                        Show original extracted text
                      </summary>
                      <p className="text-xs text-neutral-600 mt-1 pl-4 border-l-2 border-neutral-200 whitespace-pre-wrap">
                        {question.extractedText}
                      </p>
                    </details>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <Link
                    to={ROUTES.PROJECT_QUESTION_DETAIL
                      .replace(':projectId', project.id)
                      .replace(':questionId', question.id)}
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    View Details →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}